import React, { forwardRef } from 'react';
import { classNames } from '@/lib/utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helperText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, helperText, className, id, ...props }, ref) => {
        const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={inputId}
                        className="block text-sm font-medium text-content-title mb-1.5"
                    >
                        {label}
                    </label>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    className={classNames(
                        // Base styles
                        'block w-full rounded-lg border px-4 py-3',
                        'text-content-body placeholder:text-content-muted',
                        'transition-colors duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-offset-0',
                        // Touch-friendly size
                        'min-h-[48px] text-base',
                        // Border color based on error state
                        error
                            ? 'border-status-danger focus:border-status-danger focus:ring-status-danger/20'
                            : 'border-ui-border focus:border-brand-primary focus:ring-brand-primary/20',
                        // Disabled
                        props.disabled && 'bg-gray-50 cursor-not-allowed',
                        // Custom
                        className
                    )}
                    {...props}
                />
                {(error || helperText) && (
                    <p
                        className={classNames(
                            'mt-1.5 text-sm',
                            error ? 'text-status-danger' : 'text-content-muted'
                        )}
                    >
                        {error || helperText}
                    </p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
